"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SquareCheck as CheckSquare } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function ArenaSetup() {
  const shouldReduceMotion = useReducedMotion();
  const checklistItems = ["Correct inflatable for the system", "Visible condition", "Blower compatibility", "Inflation points", "Deflation points", "Bull-base opening or interface", "Cables or hoses positioned appropriately", "Space around the assembled arena"];
  const transitionConfig = {
    duration: shouldReduceMotion ? 0 : 0.3,
    ease: [0.16, 1, 0.3, 1]
  };
  const initialMotion = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 12
  };
  const inViewMotion = {
    opacity: 1,
    y: 0
  };
  return <section id="inflatablesafetyarenasetup" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Spec Header Block */}
        <motion.div initial={initialMotion} whileInView={inViewMotion} viewport={{
        once: true,
        amount: 0.2
      }} transition={transitionConfig} className="space-y-4 mb-10">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="rounded-none border-border bg-muted/60 text-muted-foreground font-mono text-xs uppercase tracking-wider px-2 py-0.5">
              Spec Sheet // Field Manual
            </Badge>
            <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
              Component Ref: ISA-01
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight text-foreground">
            Setting Up the Inflatable Safety Arena
          </h2>

          <div className="space-y-4 pt-2 text-base sm:text-lg leading-relaxed text-muted-foreground font-sans">
            <p>
              The inflatable safety arena is an important part of many mechanical bull setups.
              It should be treated as its own equipment component rather than simply as decoration
              around the bull.
            </p>
            <p>
              Before setup, identify the correct inflatable for the machine, check its visible
              condition and confirm which blower it requires. Follow the instructions supplied for
              positioning, inflation and integration with the bull base.
            </p>
          </div>
        </motion.div>

        {/* Structured Industrial Checklist Panel */}
        <motion.div initial={initialMotion} whileInView={inViewMotion} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        ...transitionConfig,
        delay: shouldReduceMotion ? 0 : 0.08
      }} className="border border-border bg-muted p-6 sm:p-8 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 pb-4 mb-6 border-b border-border">
            <h3 className="font-serif text-xl sm:text-2xl uppercase font-semibold text-foreground tracking-wide">
              Buyer Checklist
            </h3>
            <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
              Pre-Operation Protocol
            </span>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
            {checklistItems.map((item, index) => <li key={index} data-index={index} className="flex items-start gap-3 text-sm sm:text-base text-foreground font-sans">
                <span className="mt-1 shrink-0 text-foreground">
                  <CheckSquare className="size-4 text-foreground stroke-[2.2]" />
                </span>
                <span>{item}</span>
              </li>)}
          </ul>
        </motion.div>

        {/* Technical Closing Note */}
        <motion.div initial={initialMotion} whileInView={inViewMotion} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        ...transitionConfig,
        delay: shouldReduceMotion ? 0 : 0.16
      }} className="border-l-2 border-border pl-4 py-1">
          <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
            A dedicated{" "}
            <Link to="/InflatableMechanicalBullsPage" className="text-foreground underline underline-offset-4 hover:text-primary transition-colors">
              Inflatable Mechanical Bulls
            </Link>{" "}
            page covers complete bull-and-inflatable systems in more detail.
          </p>
        </motion.div>
      </div>
    </section>;
}